import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Card, Stack, Container, Typography, TextField,
    Alert, Box, CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

export default function FounderSetup2FA() {
    const [searchParams] = useSearchParams();
    const setupToken = searchParams.get('token') || '';
    const navigate = useNavigate();

    const [step, setStep] = useState('init'); // init, confirm, done
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState('');

    const handleInit = async () => {
        setLoading(true);
        setApiError('');
        try {
            const res = await syndicationApi.post('founder/setup-2fa/', {
                setup_token: setupToken,
                action: 'init',
            });
            setQrCode(res.data?.qr_code || '');
            setSecret(res.data?.secret || '');
            setStep('confirm');
        } catch (err) {
            setApiError(err.response?.data?.message || 'Failed to initialize 2FA setup.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        setLoading(true);
        setApiError('');
        try {
            await syndicationApi.post('founder/setup-2fa/', {
                setup_token: setupToken,
                action: 'confirm',
                totp_code: totpCode,
            });
            setStep('done');
        } catch (err) {
            setApiError(err.response?.data?.message || 'Invalid TOTP code.');
        } finally {
            setLoading(false);
        }
    };

    if (!setupToken) {
        return (
            <RootStyle title="Setup 2FA | Gateway X">
                <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                    <FormCard>
                        <Alert severity="error">Missing setup token. Please use the link from your email.</Alert>
                    </FormCard>
                </Container>
            </RootStyle>
        );
    }

    return (
        <RootStyle title="Setup 2FA | Gateway X">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>Setup Two-Factor Auth</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Secure your founder account with TOTP
                        </Typography>
                    </Box>

                    {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}

                    {step === 'init' && (
                        <Stack spacing={3} alignItems="center">
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Click below to generate your QR code. You'll need an authenticator app
                                (Google Authenticator, Authy, etc.).
                            </Typography>
                            <LoadingButton
                                variant="contained" size="large" loading={loading}
                                onClick={handleInit}
                                sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                            >
                                Generate QR Code
                            </LoadingButton>
                        </Stack>
                    )}

                    {step === 'confirm' && (
                        <Stack spacing={3} alignItems="center">
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Scan the QR code below with your authenticator app, then enter the 6-digit code.
                            </Typography>
                            {qrCode && (
                                <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #eee' }}>
                                    <img src={qrCode} alt="QR Code" style={{ width: 200, height: 200 }} />
                                </Box>
                            )}
                            {secret && (
                                <Typography variant="caption" color="text.secondary" sx={{
                                    p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontFamily: 'monospace',
                                    wordBreak: 'break-all', textAlign: 'center',
                                }}>
                                    Manual entry: {secret}
                                </Typography>
                            )}
                            <TextField
                                label="6-digit TOTP Code" value={totpCode}
                                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }}
                                sx={{ width: 240 }}
                            />
                            <LoadingButton
                                variant="contained" size="large" loading={loading}
                                onClick={handleConfirm} disabled={totpCode.length !== 6}
                                sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                            >
                                Verify & Complete Setup
                            </LoadingButton>
                        </Stack>
                    )}

                    {step === 'done' && (
                        <Alert severity="success">
                            2FA setup complete! You can now{' '}
                            <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                                onClick={() => navigate('/founder/login')}>
                                log in here
                            </Box>.
                        </Alert>
                    )}
                </FormCard>
            </Container>
        </RootStyle>
    );
}
